import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { BackButton, MainButton, View } from '@ds'
import { GalaxyButton, MainLayout, useConnectWallet, useDisconnectWallet, useStarAnim } from '@shared'
import { useNotClaimAvailability, useOverlayActions, useUser, useUserActions } from '@store'

import { StarAnim } from '../referrals/components/StarAnim'
import { StarAnimProvider } from '../referrals/providers/StarAnimProvider'

import borderSrc from './assets/border.svg'
import linesLeftSrc from './assets/lines_left.svg'
import linesRightSrc from './assets/lines_right.svg'
import { ConditionBall } from './components/ConditionBall'
import styles from './RewardsView.module.scss'
import { useTranslation } from 'react-i18next'
import { DonatedScreen } from './components/DonatedScreen/DonatedScreen'
import { ClaimRewardScreen } from './components/ClaimRewardScreen/ClaimRewardScreen'
import { FailedRewardScreen } from './components/FailedRewardScreen/FailedRewardScreen'
import { useTonWallet } from '@tonconnect/ui-react'
import cn from 'classnames'
import { AVAILABLE_USERS_ID } from '../../../../App'
import { ClaimedScreen } from './components/ClaimedScreen/ClaimedScreen'
import { BanScreen } from './components/BanScreen/BanScreen'

const SNAPSHOT = true
export const RewardsView = () => {
  const navigate = useNavigate()
  const [isClaimed, setIsClaimed] = useState(false)
  const [loading, setLoading] = useState(true)
  const { starAnim } = useStarAnim()
  const { t } = useTranslation()
  const { getNotClaimAvailability, donateReward, checkIfClaimed } = useUserActions()
  const { setOverlayQueue } = useOverlayActions()
  const { connect } = useConnectWallet()
  const { disconnect } = useDisconnectWallet()
  const wallet = useTonWallet()

  const profile = useUser().profile
  const availability = useNotClaimAvailability()
  const isWalletConnected = availability?.isWalletConnected ?? false

  const fetchInfo = async () => {
    await getNotClaimAvailability()
    const res = await checkIfClaimed();
    setIsClaimed(res);
    setLoading(false);
  }
  useEffect(() => {
    fetchInfo()
  }, [])

  useEffect(() => {
    if (wallet && !isWalletConnected) {
      getNotClaimAvailability()
    }
  }, [wallet, isWalletConnected])

  const connectWallet = useCallback(() => {
    if (wallet) {
      disconnect()
    }
    connect()
  },[wallet, connect, disconnect])

  const conditions = useMemo(() => [
    {
      id: 'starlog',
      done: availability?.battlepassLevelReached ?? false,
      label: 'StarLog 30 lvl',
    },
    {
      id: 'not',
      done: availability?.scoreReached ?? false,
      label: '12k $NOT',
    },
    {
      id: 'quests',
      done: availability?.allQuestsSolved ?? false,
      label: t('rewards.completeAllQuests'),
    },
    {
      id: 'wallet',
      done: isWalletConnected,
      action: { label: t('settings.wallet'), onClick: isWalletConnected ? undefined : connectWallet },
    },
  ], [availability, isWalletConnected, connectWallet])

  const isClaimAvailable = availability?.isAvailable ?? false

  const canTest = AVAILABLE_USERS_ID.includes(profile?.id ?? 0)
  const [testState, setTestState] = useState<'real' | 'conditions' | 'claim' | 'failed' | 'donated' | 'claimed' | 'ban'>('real')

  const handleDonate = () =>
    setOverlayQueue([{
      type: 'donateReward',
      payload: { onConfirm: async () => {
        await donateReward();
      } },
    }])

  const renderBody = () => {
    if (loading) return null
    if (testState === 'ban') return <BanScreen />
    if (testState === 'claimed') return <ClaimedScreen />
    if (testState === 'donated') return <DonatedScreen />
    if (testState === 'claim') return <ClaimRewardScreen amount={availability?.balance ?? 0} setIsClaimed={(val) => setIsClaimed(val)} />
    if (testState === 'failed') return <FailedRewardScreen amount={availability?.balance ?? 0} onDonate={handleDonate} />

    if (testState === 'real') {
      if (availability?.isDonated) return <DonatedScreen />
      if (availability?.isSuspended) return <BanScreen />
      if (SNAPSHOT) {
        if (isClaimed) return <ClaimedScreen />
        return isClaimAvailable ? (
          <ClaimRewardScreen amount={availability?.balance ?? 0} setIsClaimed={(res)=> {
            setIsClaimed(res)
          }}/>
        ) : (
          <FailedRewardScreen amount={availability?.balance ?? 0} onDonate={handleDonate} />
        )
      }
    }

    return (
      <>
        <div className={styles.frame}>
          <img
            src={borderSrc}
            alt=""
            className={styles.frameBorder}
            aria-hidden
          />

          <span className={styles.frameLabel}>{t('rewards.conditions')}</span>

          <div className={styles.conditions}>
            {conditions.map((condition, idx) => {
              const isLast = idx === conditions.length - 1
              return (
                <div key={condition.id} className={styles.condition}>
                  <div className={styles.ballWrapper}>
                    {!isLast && (
                      <div className={styles.connector} aria-hidden />
                    )}
                    <ConditionBall done={condition.done} />
                  </div>
                  {condition.action ? (
                    <button
                      type="button"
                      className={cn(styles.walletButton, { [styles['walletButton__done']]: condition.done })}
                      onClick={condition.action.onClick}
                    >
                      <span className={styles.walletButtonText}>
                        {condition.action.label}
                      </span>
                    </button>
                  ) : (
                    <span className={styles.conditionText}>
                      {condition.label}
                    </span>
                  )}
                </div>
              )
            })}
          </div>

          <div className={styles.timeLeft}>
            <span>{t('rewards.lastChance')}</span>
            <div className={styles.timeDotWrapper}>
              <span className={styles.timeDot} />
              <span className={styles.timeDotRipple} />
              <span className={styles.timeDotRipple} />
              <span className={styles.timeDotRipple} />
            </div>
          </div>
        </div>

        <div className={styles.amount}>{`${availability?.balance ?? 0} $NOT`}</div>

        <div className={styles.buttons}>
          <GalaxyButton
            className={styles.button}
            withGlow={false}
            onClick={handleDonate}
          >
            {t('rewards.donateTo2ndSeason')}
          </GalaxyButton>
          <GalaxyButton
            className={`${styles.button} ${styles.buttonClaim}`}
            withGlow={false}
            disabled
          >
            {t('rewards.claimReward')}
          </GalaxyButton>
        </div>
      </>
    )
  }

  return (
    <StarAnimProvider starAnim={starAnim}>
      <MainLayout showInventory={false} showNavbar={false} isNavbarFadeble>
        {(scrollRef) => (
          <>
            <BackButton onClick={() => {
              if (/Android/i.test(navigator.userAgent)) {
                window.addEventListener('popstate', () => window.location.reload(), { once: true })
              }
              navigate(-1)
            }} />
            <MainButton hidden />

            <View fadeIn className={styles.view} ref={scrollRef}>
              {canTest && (
                <div className={styles.testSwitcher}>
                  {(['real', 'conditions', 'claim', 'failed', 'donated', 'claimed', 'ban'] as const).map((state) => (
                    <button
                      key={state}
                      type="button"
                      className={cn(styles.testSwitcherButton, {
                        [styles.testSwitcherButtonActive]: testState === state,
                      })}
                      onClick={() => setTestState(state)}
                    >
                      {state}
                    </button>
                  ))}
                </div>
              )}
              <div className={styles.header}>
                <img
                  src={linesLeftSrc}
                  alt=""
                  className={styles.linesLeft}
                  aria-hidden
                />
                <img
                  src={linesRightSrc}
                  alt=""
                  className={styles.linesRight}
                  aria-hidden
                />
                <StarAnim
                  width={130}
                  height={130}
                  className={styles.starAnim}
                />
                <h1 className={styles.title}>Rewards</h1>
              </div>

              {renderBody()}
            </View>
          </>
        )}
      </MainLayout>
    </StarAnimProvider>
  )
}
